import {
    CreateResourceNotificationSettingInput,
    CreateResourceNotificationSettingMutation,
    CreateResourceNotificationSettingMutationVariables,
    DeleteResourceNotificationSettingInput,
    DeleteResourceNotificationSettingMutationVariables,
    GetResourceNotificationSettingQuery,
    GetResourceNotificationSettingQueryVariables,
    ResourceNotificationSetting,
    UpdateResourceNotificationSettingInput,
    UpdateResourceNotificationSettingMutation,
    UpdateResourceNotificationSettingMutationVariables
} from "../../../../API";
import GraphQLAPI, {
    GraphQLResult
} from "@aws-amplify/api-graphql";
import {
    createResourceNotificationSetting,
    deleteResourceNotificationSetting,
    updateResourceNotificationSetting
} from "../../../../graphql/mutations";

import { AuthIDTokenSupplierFactory } from "../../../auth/AuthIDTokenSupplierFactory";
import ClientLogger from "../../../logging/ClientLogger";
import ClientLoggerFactory from "../../../logging/ClientLoggerFactory";
import { IDTokenSupplier } from "../../../auth/IDTokenSupplier";
import { ResourceNotificationSettingDAO } from "./ResourceNotificationSettingDAO";
import { getResourceNotificationSetting } from "../../../../graphql/queries";

export class GraphQLResourceNotificationSettingDAO implements ResourceNotificationSettingDAO {
    private static readonly GET_ATTEMPT_METRIC_NAME = "GraphQLResourceNotificationSettingDAO.Get.Attempt";
    private static readonly GET_FAILURE_METRIC_NAME = "GraphQLResourceNotificationSettingDAO.Get.Failure";
    private static readonly CREATE_ATTEMPT_METRIC_NAME = "GraphQLResourceNotificationSettingDAO.Create.Attempt";
    private static readonly CREATE_FAILURE_METRIC_NAME = "GraphQLResourceNotificationSettingDAO.Create.Failure";
    private static readonly UPDATE_ATTEMPT_METRIC_NAME = "GraphQLResourceNotificationSettingDAO.Update.Attempt";
    private static readonly UPDATE_FAILURE_METRIC_NAME = "GraphQLResourceNotificationSettingDAO.Update.Failure";
    private static readonly DELETE_ATTEMPT_METRIC_NAME = "GraphQLResourceNotificationSettingDAO.Delete.Attempt";
    private static readonly DELETE_FAILURE_METRIC_NAME = "GraphQLResourceNotificationSettingDAO.Delete.Failure";

    private readonly logger: ClientLogger;
    private readonly idTokenSupplier: IDTokenSupplier;

    constructor(
        logger: ClientLogger,
        idTokenSupplier: IDTokenSupplier
    ) {
        this.logger = logger;
        this.idTokenSupplier = idTokenSupplier;
    }

    public async get(variables: GetResourceNotificationSettingQueryVariables): Promise<ResourceNotificationSetting | undefined> {
        try {
            this.logger.info(
                `Getting ResourceNotificationSetting record with variables: ${JSON.stringify(variables)}`,
                variables,
                [GraphQLResourceNotificationSettingDAO.GET_ATTEMPT_METRIC_NAME]
            );
            const authToken: string = await this.idTokenSupplier.get();
            const result: GraphQLResult<GetResourceNotificationSettingQuery> = await GraphQLAPI.graphql({
                query: getResourceNotificationSetting,
                variables,
                authToken
            }) as GraphQLResult<GetResourceNotificationSettingQuery>;
            return result.data?.getResourceNotificationSetting || undefined;
        } catch (error) {
            this.logger.error(
                `Unable to get ResourceNotificationSetting record with variables: ${JSON.stringify(variables)}`,
                error,
                [GraphQLResourceNotificationSettingDAO.GET_FAILURE_METRIC_NAME]
            );
            throw new Error(`Unable to get ResourceNotificationSetting record with variables: ${JSON.stringify(variables)}`, { cause: error });
        }
    }

    public async create(setting: CreateResourceNotificationSettingInput): Promise<ResourceNotificationSetting> {
        try {
            this.logger.info(
                `Creating ResourceNotificationSetting record with input: ${JSON.stringify(setting)}`,
                setting,
                [GraphQLResourceNotificationSettingDAO.CREATE_ATTEMPT_METRIC_NAME]
            );
            const variables: CreateResourceNotificationSettingMutationVariables = { input: setting };
            const authToken: string = await this.idTokenSupplier.get();
            const result: GraphQLResult<CreateResourceNotificationSettingMutation> = await GraphQLAPI.graphql({
                query: createResourceNotificationSetting,
                variables,
                authToken
            }) as GraphQLResult<CreateResourceNotificationSettingMutation>;
            const createdSetting = result.data?.createResourceNotificationSetting;
            if (!createdSetting) {
                throw new Error(`Unable to create ResourceNotificationSetting record with input: ${JSON.stringify(setting)}`);
            }
            return createdSetting;
        } catch (error) {
            this.logger.error(
                `Unable to create ResourceNotificationSetting record with input: ${JSON.stringify(setting)}`,
                error,
                [GraphQLResourceNotificationSettingDAO.CREATE_FAILURE_METRIC_NAME]
            );
            throw new Error(`Unable to create ResourceNotificationSetting record with input: ${JSON.stringify(setting)}`, { cause: error });
        }
    }

    public async update(setting: UpdateResourceNotificationSettingInput): Promise<ResourceNotificationSetting> {
        try {
            this.logger.info(
                `Updating ResourceNotificationSetting record with input: ${JSON.stringify(setting)}`,
                setting,
                [GraphQLResourceNotificationSettingDAO.UPDATE_ATTEMPT_METRIC_NAME]
            );
            const variables: UpdateResourceNotificationSettingMutationVariables = {
                input: {
                    notificationType: setting.notificationType,
                    resourceId: setting.resourceId,
                    resourceType: setting.resourceType,
                    _version: setting._version,
                    enabled: setting.enabled,
                    recipients: setting.recipients?.map(recipient => ({
                        enabled: recipient.enabled,
                        notificationChannels: recipient.notificationChannels,
                        recipientId: recipient.recipientId,
                        recipientIdType: recipient.recipientIdType
                    }))
                }
            };
            const authToken: string = await this.idTokenSupplier.get();
            const result: GraphQLResult<UpdateResourceNotificationSettingMutation> = await GraphQLAPI.graphql({
                query: updateResourceNotificationSetting,
                variables,
                authToken
            }) as GraphQLResult<UpdateResourceNotificationSettingMutation>;
            const updatedSetting = result.data?.updateResourceNotificationSetting;
            if (!updatedSetting) {
                throw new Error(`Unable to update ResourceNotificationSetting record with input: ${JSON.stringify(setting)}`);
            }
            return updatedSetting;
        } catch (error) {
            this.logger.error(
                `Unable to update ResourceNotificationSetting record with input: ${JSON.stringify(setting)}`,
                error,
                [GraphQLResourceNotificationSettingDAO.UPDATE_FAILURE_METRIC_NAME]
            );
            throw new Error(`Unable to update ResourceNotificationSetting record with input: ${JSON.stringify(setting)}`, { cause: error });
        }
    }

    public async delete(input: DeleteResourceNotificationSettingInput): Promise<void> {
        try {
            this.logger.info(
                `Deleting ResourceNotificationSetting record with input: ${JSON.stringify(input)}`,
                input,
                [GraphQLResourceNotificationSettingDAO.DELETE_ATTEMPT_METRIC_NAME]
            );
            const variables: DeleteResourceNotificationSettingMutationVariables = { input };
            const authToken: string = await this.idTokenSupplier.get();
            await GraphQLAPI.graphql({
                query: deleteResourceNotificationSetting,
                variables,
                authToken
            });
        } catch (error) {
            this.logger.error(
                `Unable to delete ResourceNotificationSetting record with input: ${JSON.stringify(input)}`,
                error,
                [GraphQLResourceNotificationSettingDAO.DELETE_FAILURE_METRIC_NAME]
            );
            throw new Error(`Unable to delete ResourceNotificationSetting record with input: ${JSON.stringify(input)}`, { cause: error });
        }
    }
}

export const graphQLResourceNotificationSettingDAO: ResourceNotificationSettingDAO = new GraphQLResourceNotificationSettingDAO(
    ClientLoggerFactory.getClientLogger("GraphQLResourceNotificationSettingDAO"),
    AuthIDTokenSupplierFactory.getInstance()
);